/**
 * Тонкая обёртка над fetch для same-origin /api/* запросов.
 * httpOnly-cookie fastapi-users отправляются автоматически (credentials: include).
 *
 * Префикс API настраивается через VITE_API_BASE (default "/api"). На prod-build
 * для path-prefixed deploy выставляем VITE_API_BASE=/v2/api — тогда вызовы
 * api("/api/users/me") уезжают на /v2/api/users/me, а nginx уже стрипает /v2.
 */

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

/** AuthProvider слушает это событие и переводит приложение на экран входа. */
export const AUTH_EXPIRED_EVENT = "kb:auth-expired";


function resolvePath(path: string): string {
  // Принимаем оба варианта: "/api/foo" и просто "foo". Если путь начинается
  // с "/api/" — заменяем префикс на API_BASE; иначе считаем что это сразу
  // sub-path после API_BASE.
  if (path.startsWith("/api/")) {
    return API_BASE + path.slice(4);
  }
  if (path.startsWith("/api")) {
    return API_BASE + path.slice(4);
  }
  return path;
}


export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `HTTP ${status}`);
    this.name = "ApiError";
  }
}

function apiErrorMessage(status: number, body: unknown): string {
  if (body && typeof body === "object") {
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) return detail.trim();

    const message = (body as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message.trim();
  }
  if (status === 401) return "Сессия истекла. Войдите снова.";
  if (status === 403) return "Недостаточно прав для этого действия.";
  return `HTTP ${status}`;
}

interface ApiOptions extends RequestInit {
  /** Если true — не парсить тело как JSON, вернуть Response. */
  raw?: boolean;
}

export async function api<T = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const { raw, headers, ...rest } = opts;

  const res = await fetch(resolvePath(path), {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(rest.body && !(rest.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...headers,
    },
    ...rest,
  });

  if (raw) {
    if (res.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    return res as unknown as T;
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // тело не json — оставляем null
    }
    if (res.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    throw new ApiError(res.status, body, apiErrorMessage(res.status, body));
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
